/**
 * FogSystem.ts
 * Maintains the per-tile fog-of-war state for every cell in the grid.
 *
 * Three states (§ FogState in types.ts):
 *   Unexplored — tile has never been seen; rendered opaque black
 *   Explored   — seen before but not currently visible; rendered dark+transparent
 *   Visible    — inside a living unit's sight radius right now
 *
 * Recalculates every UPDATE_INTERVAL seconds (not every frame) for performance.
 * Sets dirty = true after each recalculation so FogRenderer knows to rebuild.
 *
 * Algorithm:
 *   1. Demote all Visible tiles → Explored (units may have moved).
 *   2. For each living revealing unit, mark all tiles within sightRadius as Visible.
 *      Uses Chebyshev-ish circle via Math.hypot check for a natural oval.
 */

import { FogState } from '../types';
import type { TileMap } from '../map/TileMap';
import type { Unit } from '../entities/Unit';

/** Seconds between fog recalculations — matches FOG_UPDATE_INTERVAL in constants. */
const UPDATE_INTERVAL = 0.5;

export class FogSystem {
  /** Flat array of FogState indexed by (row * width + col). */
  readonly fog: FogState[];

  /** True after each recalculate; cleared by FogRenderer after its rebuild. */
  dirty = false;

  // Start at UPDATE_INTERVAL so the very first update() call triggers recalculate,
  // ensuring the beacon area is already revealed on frame 1.
  private elapsed = UPDATE_INTERVAL;

  constructor(private readonly map: TileMap) {
    this.fog = new Array(map.width * map.height).fill(FogState.Unexplored);
    // Beacon is visible from frame 0 — no flicker on load.
    const b = map.beacon;
    for (let dr = 0; dr <= 1; dr++) {
      for (let dc = 0; dc <= 1; dc++) {
        this.fog[(b.row + dr) * map.width + (b.col + dc)] = FogState.Visible;
      }
    }
  }

  /**
   * Advance the fog timer. Triggers a full recalculate every UPDATE_INTERVAL s.
   * Call once per game update frame with un-scaled real-time delta.
   */
  update(revealingUnits: Unit[], delta: number): void {
    this.elapsed += delta;
    if (this.elapsed < UPDATE_INTERVAL) return;
    this.elapsed -= UPDATE_INTERVAL;
    this.recalculate(revealingUnits);
  }

  /** Query the fog state for a single tile (safe; returns Unexplored when OOB). */
  getState(col: number, row: number): FogState {
    if (!this.map.inBounds(col, row)) return FogState.Unexplored;
    return this.fog[row * this.map.width + col];
  }

  /**
   * Immediately reveal all Unexplored tiles within `radius` of (col, row) to
   * at least FogState.Explored. Called by the Mystic's Prophecy skill.
   * Already-Explored or Visible tiles are left untouched.
   */
  revealRadius(col: number, row: number, radius: number): void {
    const r = Math.ceil(radius);
    const { width } = this.map;
    for (let dr = -r; dr <= r; dr++) {
      for (let dc = -r; dc <= r; dc++) {
        if (Math.hypot(dc, dr) > radius) continue;
        const c  = col + dc;
        const ro = row + dr;
        if (!this.map.inBounds(c, ro)) continue;
        const idx = ro * width + c;
        if (this.fog[idx] === FogState.Unexplored) {
          this.fog[idx] = FogState.Explored;
        }
      }
    }
    this.dirty = true;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private recalculate(revealingUnits: Unit[]): void {
    const { width } = this.map;

    // Step 1 — demote currently-Visible tiles back to Explored.
    for (let i = 0; i < this.fog.length; i++) {
      if (this.fog[i] === FogState.Visible) this.fog[i] = FogState.Explored;
    }

    // Step 2 — light up every tile within each living revealing unit's sight radius.
    for (const unit of revealingUnits) {
      if (!unit.alive) continue;
      const sightRadius = Math.max(0.75, unit.sightRadius);
      const r  = Math.ceil(sightRadius);
      const uc = unit.gridPos.col;
      const ur = unit.gridPos.row;

      for (let dr = -r; dr <= r; dr++) {
        for (let dc = -r; dc <= r; dc++) {
          if (Math.hypot(dc, dr) > sightRadius) continue;
          const c = uc + dc;
          const ro = ur + dr;
          if (!this.map.inBounds(c, ro)) continue;
          this.fog[ro * width + c] = FogState.Visible;
        }
      }
    }

    // Step 3 — the Beacon is always visible regardless of unit positions.
    // It's the heart of the story and should never disappear into darkness.
    const b = this.map.beacon;
    for (let dr = 0; dr <= 1; dr++) {
      for (let dc = 0; dc <= 1; dc++) {
        const idx = (b.row + dr) * width + (b.col + dc);
        if (idx >= 0 && idx < this.fog.length) {
          this.fog[idx] = FogState.Visible;
        }
      }
    }

    this.dirty = true;
  }
}
